import type {
  YardCarrierGroupPayload,
  YardCarrierGroupRule,
  YardCarrierGroupRulePayload,
  YardCarrierGroupRuleType,
  YardCatalogItem,
} from 'contracts/VehicleJourney';
import { Check, Pencil, Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { apiErrorHandler, notify } from 'services';
import { Button } from 'styles/components';
import { normalizeCode, sanitizeFlowName } from './config';

interface Props {
  groups: YardCatalogItem[];
  carriers: Array<{ id: number; name: string }>;
  createGroup: (payload: YardCarrierGroupPayload) => Promise<YardCatalogItem>;
  updateGroup: (
    id: number,
    payload: YardCarrierGroupPayload
  ) => Promise<YardCatalogItem>;
  getRules: (groupId: number) => Promise<YardCarrierGroupRule[]>;
  createRule: (
    groupId: number,
    payload: YardCarrierGroupRulePayload
  ) => Promise<YardCarrierGroupRule>;
  updateRule: (
    groupId: number,
    id: number,
    payload: YardCarrierGroupRulePayload
  ) => Promise<YardCarrierGroupRule>;
  onGroupSaved: (group: YardCatalogItem) => void;
}

const emptyGroup = (): YardCarrierGroupPayload => ({
  code: '',
  name: '',
  active: true,
});

const emptyRule = (): YardCarrierGroupRulePayload => ({
  ruleType: 'CNPJ_ROOT',
  value: '',
  priority: 100,
  active: true,
});

const RULE_LABELS: Record<YardCarrierGroupRuleType, string> = {
  CARRIER_ID: 'Transportadora específica',
  CNPJ_ROOT: 'Raiz do CNPJ',
  UNMATCHED: 'Demais transportadoras',
};

export const CarrierGroupsTab: React.FC<Props> = ({
  groups,
  carriers,
  createGroup,
  updateGroup,
  getRules,
  createRule,
  updateRule,
  onGroupSaved,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(
    groups[0]?.id || null
  );
  const [draft, setDraft] = useState<YardCarrierGroupPayload>(() =>
    groups[0] ? { ...groups[0] } : emptyGroup()
  );
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState<YardCarrierGroupRule[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [isRuleFormOpen, setIsRuleFormOpen] = useState(false);
  const [ruleDraft, setRuleDraft] =
    useState<YardCarrierGroupRulePayload>(emptyRule);
  const [ruleErrors, setRuleErrors] = useState<Record<string, string>>({});
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [isSavingRule, setIsSavingRule] = useState(false);

  useEffect(() => {
    if (!selectedId) {
      setRules([]);
      return;
    }

    let mounted = true;
    setIsLoadingRules(true);
    getRules(selectedId)
      .then((loadedRules) => mounted && setRules(loadedRules))
      .catch((error) => notify('error', apiErrorHandler(error).errorMessage))
      .finally(() => mounted && setIsLoadingRules(false));

    return () => {
      mounted = false;
    };
  }, [getRules, selectedId]);

  const visibleGroups = useMemo(
    () =>
      groups.filter((group) =>
        `${group.code} ${group.name}`
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())
      ),
    [groups, search]
  );

  const closeRuleForm = () => {
    setSelectedRuleId(null);
    setRuleDraft(emptyRule());
    setRuleErrors({});
    setIsRuleFormOpen(false);
  };

  const startNewRule = () => {
    setSelectedRuleId(null);
    setRuleDraft(emptyRule());
    setRuleErrors({});
    setIsRuleFormOpen(true);
  };

  const selectGroup = (group: YardCatalogItem) => {
    setSelectedId(group.id);
    setDraft({ code: group.code, name: group.name, active: group.active });
    setErrors({});
    closeRuleForm();
  };

  const startNewGroup = () => {
    setSelectedId(null);
    setDraft(emptyGroup());
    setErrors({});
    closeRuleForm();
  };

  const updateDraft = <K extends keyof YardCarrierGroupPayload>(
    field: K,
    value: YardCarrierGroupPayload[K]
  ) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const saveGroup = async () => {
    const nextErrors: Record<string, string> = {};
    if (!draft.name.trim()) nextErrors.name = 'Informe o nome do grupo.';
    if (!draft.code.trim()) nextErrors.code = 'Informe o código do grupo.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        ...draft,
        code: draft.code.trim(),
        name: draft.name.trim(),
      };
      const savedGroup = selectedId
        ? await updateGroup(selectedId, payload)
        : await createGroup(payload);
      onGroupSaved(savedGroup);
      selectGroup(savedGroup);
      notify('success', 'Grupo de transportadora salvo com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const selectRule = (rule: YardCarrierGroupRule) => {
    setSelectedRuleId(rule.id);
    setRuleDraft({
      ruleType: rule.ruleType,
      value: rule.value,
      priority: rule.priority,
      active: rule.active,
    });
    setRuleErrors({});
    setIsRuleFormOpen(true);
  };

  const updateRuleDraft = <K extends keyof YardCarrierGroupRulePayload>(
    field: K,
    value: YardCarrierGroupRulePayload[K]
  ) => {
    setRuleDraft((current) => ({ ...current, [field]: value }));
    setRuleErrors((current) => ({ ...current, [field]: '' }));
  };

  const saveRule = async () => {
    if (!selectedId || isSavingRule) return;
    const nextErrors: Record<string, string> = {};
    if (
      ruleDraft.ruleType === 'CNPJ_ROOT' &&
      !/^\d{8}$/.test(ruleDraft.value || '')
    ) {
      nextErrors.value = 'A raiz do CNPJ deve possuir 8 dígitos.';
    }
    if (
      ruleDraft.ruleType === 'CARRIER_ID' &&
      !/^\d+$/.test((ruleDraft.value || '').trim())
    ) {
      nextErrors.value = 'Selecione uma transportadora.';
    }
    setRuleErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setIsSavingRule(true);
    try {
      const payload: YardCarrierGroupRulePayload = {
        ...ruleDraft,
        value:
          ruleDraft.ruleType === 'UNMATCHED'
            ? null
            : ruleDraft.value?.trim() || null,
      };
      const savedRule = selectedRuleId
        ? await updateRule(selectedId, selectedRuleId, payload)
        : await createRule(selectedId, payload);
      setRules((current) =>
        [...current.filter((rule) => rule.id !== savedRule.id), savedRule].sort(
          (a, b) => a.priority - b.priority || a.id - b.id
        )
      );
      closeRuleForm();
      notify('success', 'Regra de agrupamento salva com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingRule(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-205px)] flex-1 overflow-hidden">
      <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Grupos</h2>
            <button
              aria-label="Novo grupo de transportadora"
              className="flex h-7 w-7 items-center justify-center rounded bg-[#EE7C2C] text-white"
              onClick={startNewGroup}
              type="button"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="relative">
            <Search
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
              size={15}
            />
            <input
              aria-label="Filtrar grupos"
              className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
              placeholder="Filtrar..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visibleGroups.map((group) => (
            <button
              aria-pressed={group.id === selectedId}
              className={`relative block w-full border-b border-slate-200 p-3 text-left transition-colors ${
                group.id === selectedId ? 'bg-orange-50' : 'hover:bg-slate-50'
              }`}
              key={group.id}
              onClick={() => selectGroup(group)}
              type="button"
            >
              {group.id === selectedId && (
                <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
              )}
              <div className="flex items-start justify-between gap-2">
                <strong className="truncate text-sm text-slate-800">
                  {group.name}
                </strong>
                <span
                  className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                    group.active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {group.active ? 'ATIVO' : 'INATIVO'}
                </span>
              </div>
              <span className="mt-0.5 block truncate font-mono text-[11px] font-semibold text-slate-500">
                {group.code}
              </span>
            </button>
          ))}
          {!visibleGroups.length && (
            <p className="p-4 text-center text-xs text-slate-500">
              Nenhum grupo encontrado.
            </p>
          )}
        </div>
      </aside>

      <section className="min-w-0 flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl rounded border border-slate-200 bg-white shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {selectedId ? 'Editar grupo' : 'Novo grupo'}
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                Grupos ativos ficam disponíveis na configuração dos fluxos.
              </p>
            </div>
            <Button
              disabled={isSaving}
              mood="primary"
              onClick={saveGroup}
              size="small"
              type="button"
            >
              <Check size={16} /> {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </header>
          <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Nome
              </span>
              <input
                aria-label="Nome do grupo"
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                maxLength={100}
                value={draft.name}
                onChange={(event) => {
                  const name = sanitizeFlowName(event.target.value);
                  updateDraft('name', name);
                  updateDraft('code', normalizeCode(name));
                }}
              />
              {errors.name && (
                <span className="text-xs font-bold italic text-red-600">
                  {errors.name}
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Código
              </span>
              <input
                aria-label="Código do grupo"
                className="rounded border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-[#EE7C2C]"
                maxLength={50}
                value={draft.code}
                onChange={(event) =>
                  updateDraft('code', normalizeCode(event.target.value))
                }
              />
              {errors.code && (
                <span className="text-xs font-bold italic text-red-600">
                  {errors.code}
                </span>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                aria-label="Grupo ativo"
                checked={draft.active}
                className="accent-[#EE7C2C]"
                onChange={(event) =>
                  updateDraft('active', event.target.checked)
                }
                type="checkbox"
              />
              Grupo ativo
            </label>
          </div>

          <div className="border-t border-slate-200 p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Regras de agrupamento
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  A resolução considera transportadora, raiz do CNPJ e, por
                  último, as demais transportadoras.
                </p>
              </div>
              <button
                className="flex items-center gap-1 rounded bg-[#EE7C2C] px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                disabled={!selectedId}
                onClick={startNewRule}
                type="button"
              >
                <Plus size={15} /> Nova regra
              </button>
            </div>

            {!selectedId ? (
              <p className="rounded border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">
                Salve o grupo para configurar suas regras.
              </p>
            ) : (
              <>
                <div className="mb-4 overflow-hidden rounded border border-slate-200">
                  {isLoadingRules ? (
                    <p className="p-4 text-center text-xs text-slate-500">
                      Carregando regras...
                    </p>
                  ) : rules.length ? (
                    rules.map((rule) => (
                      <button
                        aria-label={`Editar regra ${RULE_LABELS[rule.ruleType]}`}
                        className={`grid w-full grid-cols-[1fr_1fr_auto_auto] items-center gap-3 border-b border-slate-200 p-3 text-left text-xs last:border-0 ${
                          selectedRuleId === rule.id
                            ? 'bg-orange-50'
                            : 'hover:bg-slate-50'
                        }`}
                        key={rule.id}
                        onClick={() => selectRule(rule)}
                        type="button"
                      >
                        <strong className="text-slate-800">
                          {RULE_LABELS[rule.ruleType]}
                        </strong>
                        <span className="truncate text-slate-600">
                          {rule.ruleType === 'UNMATCHED'
                            ? 'N/A'
                            : rule.ruleType === 'CARRIER_ID'
                              ? (carriers.find(
                                  (carrier) => String(carrier.id) === rule.value
                                )?.name ?? rule.value)
                              : rule.value}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                            rule.active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {rule.active ? 'ATIVA' : 'INATIVA'}
                        </span>
                        <Pencil className="text-[#EE7C2C]" size={14} />
                      </button>
                    ))
                  ) : (
                    <p className="p-4 text-center text-xs text-slate-500">
                      Nenhuma regra configurada para este grupo.
                    </p>
                  )}
                </div>

                {isRuleFormOpen && (
                  <div className="rounded border border-slate-200 bg-slate-50 p-4">
                    <h4 className="mb-3 text-sm font-bold text-slate-800">
                      {selectedRuleId ? 'Editar regra' : 'Nova regra'}
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Tipo
                        </span>
                        <select
                          aria-label="Tipo da regra"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                          value={ruleDraft.ruleType}
                          onChange={(event) => {
                            updateRuleDraft(
                              'ruleType',
                              event.target.value as YardCarrierGroupRuleType
                            );
                            updateRuleDraft(
                              'value',
                              event.target.value === 'UNMATCHED' ? null : ''
                            );
                          }}
                        >
                          <option value="CARRIER_ID">
                            Transportadora específica
                          </option>
                          <option value="CNPJ_ROOT">Raiz do CNPJ</option>
                          <option value="UNMATCHED">
                            Demais transportadoras
                          </option>
                        </select>
                      </label>

                      {ruleDraft.ruleType === 'CARRIER_ID' && (
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Transportadora
                          </span>
                          <select
                            aria-label="Transportadora"
                            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={ruleDraft.value || ''}
                            onChange={(event) =>
                              updateRuleDraft('value', event.target.value)
                            }
                          >
                            <option value="">
                              Selecione uma transportadora
                            </option>
                            {carriers.map((carrier) => (
                              <option key={carrier.id} value={carrier.id}>
                                {carrier.name}
                              </option>
                            ))}
                          </select>
                          {ruleErrors.value && (
                            <span className="text-xs font-bold italic text-red-600">
                              {ruleErrors.value}
                            </span>
                          )}
                        </label>
                      )}

                      {ruleDraft.ruleType === 'CNPJ_ROOT' && (
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Raiz do CNPJ
                          </span>
                          <input
                            aria-label="Raiz do CNPJ"
                            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                            inputMode="numeric"
                            maxLength={8}
                            placeholder="8 primeiros dígitos"
                            value={ruleDraft.value || ''}
                            onChange={(event) =>
                              updateRuleDraft(
                                'value',
                                event.target.value.replace(/\D/g, '')
                              )
                            }
                          />
                          {ruleErrors.value && (
                            <span className="text-xs font-bold italic text-red-600">
                              {ruleErrors.value}
                            </span>
                          )}
                        </label>
                      )}

                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Prioridade
                        </span>
                        <input
                          aria-label="Prioridade da regra"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                          min={0}
                          type="number"
                          value={ruleDraft.priority}
                          onChange={(event) =>
                            updateRuleDraft(
                              'priority',
                              Number(event.target.value)
                            )
                          }
                        />
                      </label>
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          aria-label="Regra ativa"
                          checked={ruleDraft.active}
                          className="accent-[#EE7C2C]"
                          onChange={(event) =>
                            updateRuleDraft('active', event.target.checked)
                          }
                          type="checkbox"
                        />
                        Regra ativa
                      </label>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                      <Button
                        disabled={isSavingRule}
                        mood="light"
                        onClick={closeRuleForm}
                        size="small"
                        type="button"
                      >
                        Cancelar
                      </Button>
                      <Button
                        disabled={isSavingRule}
                        mood="primary"
                        onClick={saveRule}
                        size="small"
                        type="button"
                      >
                        <Check size={16} />{' '}
                        {isSavingRule ? 'Salvando...' : 'Salvar regra'}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
